import R from 'ramda';
import { replace, goBack } from 'react-router-redux';
import { triggerNoAccessWarning, checkLocAccess, getFeatureAccess } from './accessCheckHelperFns';
const isNilOrEmpty = R.either(R.isNil, R.isEmpty);

const accessCheck = ({ getState, dispatch }) => next => action => {
  const { type, payload } = action;
  const { user, contracting } = getState();
  const companyType = user.toJS().user.type;
  const locAccess = user.toJS().locAccess;
  const getContractingAccess = locId => getFeatureAccess(getState(), locId, 'contracting');
  const checkLocContractingWriteAccess = locId => checkLocAccess(getState(), locId, 'contracting', 'write');
  const checkLocContractingAccess = locId => checkLocAccess(getState(), locId, 'contracting', 'read');
  const unApprovedShellAccount = user.toJS().unApprovedShellAccount;
  if (type === 'GETTING_CONTRACTING_YEARS') {
    const noContractingAccess = R.isEmpty(getContractingAccess());
    if (noContractingAccess && !unApprovedShellAccount) return dispatch(triggerNoAccessWarning({ featureName: 'contracting' }));
  }
  const actionsToCheck = {
    'INITIATE_ADD_EDIT_CONTRACT_DIALOG_WITH_TYPE': 'edit this document',
    'SEND_UPDATES_IN_PUBLISH_DRAFT_DIALOG': 'send updates',
    'OPEN_AS_SAVE_CONTRACT_AS_TEMPLATE': 'save this document as a template',
    '[contractTemplate]TOGGLE_CREATE_DRAFT_DIALOG': 'edit this document',
    'CHANGE_RESEND_CONTRACT_DIALOG_STATE': 'resend this contract',
    'CHANGE_CONTRACT_STATE_STARTING': 'update the state of this contract',
    'DISCARD_SELECTED_IN_REQUESTS_WITH_SAME_NAME_DIALOG': 'discard contract requests',
  };
  if (actionsToCheck[type]) {
    const locId = contracting.contractActivitiesProducts.getIn(['leftNav', 'locationId']);
    const readOnly = (locId && companyType === 'Supplier') ? !checkLocContractingWriteAccess(locId) : !R.contains('write', getContractingAccess());
    if (readOnly) {
      return dispatch(triggerNoAccessWarning({
        featureName: 'contracting',
        message: `You will need write access in order to ${actionsToCheck[type]}.`,
      }));
    }
  }
  if (type === 'UPDATE_TEMPLATE_PROPS_IN_TEMPLATE_LIST' || type === 'UPDATE_DRAFT_PROPS_IN_TEMPLATE_LIST') {
    const { templateId, draftId, data: { showEditNameConfirmButton, showDeleteConfirmButton } } = payload;
    if (showEditNameConfirmButton || showDeleteConfirmButton) {
      const docs = contracting.templateList.get(templateId ? 'templates' : 'drafts').toJS();
      const doc = templateId ? R.find(R.propEq('_id', templateId), docs) : R.find(R.propEq('originalContractId', draftId), docs);
      const locId = R.propOr('', 'locationId', doc);
      const readOnly = !checkLocContractingWriteAccess(locId);
      if (readOnly) {
        return dispatch(triggerNoAccessWarning({
          featureName: 'contracting',
          message: `You will need write access in order to ${showEditNameConfirmButton ? 'edit' : 'delete'} this document.`,
        }));
      }
    }
  }
  if (type === '[template-list]UPDATE_PROPS_IN_TEMPLATE_LIST' && payload.sendTemplateDialogOpen) {
    const { templateToBeSent, templateGroupToBeSent } = payload;
    if (templateToBeSent && templateToBeSent.locationId) {
      const readOnly = !checkLocContractingWriteAccess(templateToBeSent.locationId);
      if (readOnly) {
        return dispatch(triggerNoAccessWarning({
          featureName: 'contracting',
          message: `You will need write access in order to send rates.`,
        }));
      }
    }
    if (templateGroupToBeSent) {
      const locationIds = R.pluck('locationId', templateGroupToBeSent.templates);
      const noAccess = R.any(locId => !checkLocContractingWriteAccess(locId), locationIds);
      if (noAccess) return dispatch(triggerNoAccessWarning({ featureName: 'contracting' }));
    }
  }
  // Below blocks are adding access information to documents
  if (type === 'TEMPLATES_LOADED_IN_TEMPLATE_LIST' || type === '{template-list} REFRESH_TEMPLATE_LIST_SUCCESS') {
    const updatedTemplates = payload.templates.map(template => {
      return { ...template, access: getContractingAccess(template.locationId) };
    });
    const updatedDrafts = payload.drafts.map(draft => {
      return { ...draft, access: getContractingAccess(draft.locationId) };
    });
    return next({ ...action, payload: { ...payload, templates: updatedTemplates, drafts: updatedDrafts } });
  }
  if (type === 'LOADED_TEMPLATES_SUPPLIER_SEND_TEMPLATE_WIZARD') {
    const updatedTemplates = payload.templates.map(template => {
      return { ...template, access: getContractingAccess(template.locationId) };
    });
    return next({ ...action, payload: { ...payload, templates: updatedTemplates } });
  }
  if (type === '[contract-list]LOAD_CONTRACT_LIST_SUCCESS' || type === '[supplier-wizard]GET_RESPONDED_CONTRACT_LIST_SUCCESS') {
    const { list } = payload;
    const updatedList = list.map(contract => ({
      ...contract,
      access: unApprovedShellAccount ? ['read', 'write'] : getContractingAccess(contract.locationId),
    }));
    return next({ ...action, payload: { ...payload, list: updatedList } });
  }
  const loadLocationsToTemplateList = type === 'GET_SUPPLIER_LOCATIONS_LIST_IN_TEMPLATE_LIST';
  const loadLocationsToSupplierWizard = type === 'LOCATIONS_LOADED_SUPPLIER_SEND_TEMPLATE_WIZARD';
  // FOR CREATE TEMPLATE DRAFT
  if (loadLocationsToTemplateList && !isNilOrEmpty(payload.locations)) {
    const updatedLocations = payload.locations.map(loc => {
      return { ...loc, access: getContractingAccess(loc.locationId) };
    });
    return next({ ...action, payload: { ...payload, locations: updatedLocations } });
  }
  // FOR SELECTING TEMPLATE IN SUPPLIER SEND RATES WIZARD
  if (loadLocationsToSupplierWizard && !isNilOrEmpty(payload.locations)) {
    const updatedLocations = payload.locations.map(loc => {
      return { ...loc, access: getContractingAccess(loc.id) };
    });
    return next({ ...action, payload: { ...payload, locations: updatedLocations } });
  }

  return next(action);
};

export default accessCheck;



// WEBPACK FOOTER //
// ./src/app/middleware/accessCheck.js